function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }

/**
 * @file 发送上报
 * @author dafo<huanghouin@baijiahulian.com>
 * @module util/request
 */
import { toQuery } from './json';
import delay from './delay';
import storage from './storage';
import { Config, extendCommonField, isReady, onReady, publicParams } from './config';
import xhr from './xhr';
/**
 * 由于不同类型的日志需要上报给不同的域名(gsxtj或者qingzhou)，在此动态获取获取上报的域名
 *
 * @param {Object} jsonOrString 上报参数对象, 沿用原有的变量命名
 * @returns {Array.<string>} 上报域名数组
 */

function getRequestDomains(jsonOrString) {
  if (jsonOrString.req_type === 'page_load') {
    return [Config.DOMAIN, Config.QINGZHOU_DOMAIN];
  }

  if (jsonOrString.req_type === 'api_call' || jsonOrString.metric) {
    return [Config.QINGZHOU_DOMAIN];
  }

  return [Config.DOMAIN];
}
/**
 * 发送上报请求
 *
 * @function request.post
 * @param {string} url 上报地址
 * @param {Object|string} jsonOrString 键值对对象或字符串
 * @param {Function=} callback 上报后的回调
 * @param {Array=} data post 的数据
 */


export var post = function post(url, jsonOrString, callback) {
  var data = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
  var domains = getRequestDomains(jsonOrString); // 带有abtest等公共参数，目前只发送给哈勃大数据服务，不发送到轻舟（除了perf.gif）

  if (domains.includes(Config.DOMAIN)) {
    Object.assign(jsonOrString, publicParams);
  } // 这里的 type 是上报类型对应的地址，比如 view 事件，对应的默认就是 pv0.gif 。


  var query = toQuery(extendCommonField(jsonOrString, {
    type: url
  }));

  if (query) {
    url += "?".concat(query);
  }

  storage.save(url, data);
  storage.pending(url); // 静态文件是否通过file协议

  var isFileProtocol = document && document.location.protocol === 'file:';
  domains.forEach(function (domain) {
    // 通过file协议访问时添加https协议
    var targetURL = isFileProtocol ? "https:".concat(domain).concat(url) : domain + url;
    var isSuccess = data.length ? navigator.sendBeacon(targetURL, JSON.stringify(data)) : navigator.sendBeacon(targetURL);

    if (isSuccess && navigator.onLine) {
      storage.remove(url, data);
      storage.resend(post);
    }

    callback && callback();
  });
};
var delayPost = delay(post);
/**
 * 发送上报请求
 *
 * @function request.get
 * @param {string} url 上报地址
 * @param {Object|string} jsonOrString 键值对对象或字符串
 * @param {Function=} callback 上报后的回调
 */

export var get = function get(url, jsonOrString, callback) {
  var domains = getRequestDomains(jsonOrString); // 带有abtest等公共参数，目前只发送给哈勃大数据服务，不发送到轻舟（除了perf.gif）

  if (domains.includes(Config.DOMAIN)) {
    Object.assign(jsonOrString, publicParams);
  } // TODO 在这里计算 req_idx


  var query = toQuery(extendCommonField(jsonOrString, {
    type: url
  }));

  if (query) {
    url += "?".concat(query);
  }

  storage.save(url); // 静态文件是否通过file协议

  var isFileProtocol = document && document.location.protocol === 'file:';
  domains.forEach(function (domain) {
    // 通过file协议访问时添加https协议
    var targetURL = isFileProtocol ? "https:".concat(domain).concat(url) : domain + url;
    xhr(targetURL, function (isSuccess) {
      if (isSuccess) {
        storage.remove(url);
        storage.resend(get);
      }

      callback && callback();
    });
    storage.pending(url);
  });
};
var delayGet = delay(get);
/**
 * 发送上报请求
 *
 * @param {string=} verb HTTP Verb
 * @param {boolean} isRunAtOnce 是否立即上报
 * @return {Function} 选择到的上报提交方法
 */

export default function request(verb, isRunAtOnce) {
  var isImmediateSend = isRunAtOnce || !Config.DELAY;
  var requester = String(verb).toLowerCase() === 'post' && navigator.sendBeacon ? isImmediateSend ? post : delayPost : isImmediateSend ? get : delayGet;

  if (isReady()) {
    return requester;
  }

  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return onReady(function () {
      return requester.apply(void 0, args);
    });
  };
}
/**
 * 解析headers
 * @param {Object} headers 请求头
 * @return 解析后的对象
 */

function parseHeaders(headers) {
  var parsed = {};
  var key;
  var val;
  var i;

  if (!headers) {
    return parsed;
  }

  headers.split('\n').forEach(function (line) {
    i = line.indexOf(':');
    key = line.substring(0, i).trim().toLowerCase();
    val = line.substring(i + 1).trim();

    if (key) {
      if (key === 'set-cookie') {
        // @ts-ignore
        parsed[key] = (parsed[key] ? parsed[key] : []).concat([val]);
      } else {
        parsed[key] = parsed[key] ? "".concat(parsed[key], ", ").concat(val) : val;
      }
    }
  });
  return parsed;
}

function encode(val) {
  return encodeURIComponent(val).replace(/%3A/gi, ':').replace(/%24/g, '$').replace(/%2C/gi, ',').replace(/%20/g, '+').replace(/%5B/gi, '[').replace(/%5D/gi, ']');
}
/**
 * 判断是否是合法的 URLSearchParams 对象
 * @param {Object} params 参数
 * @return {boolean} 确认结果
 */


function isURLSearchParams(params) {
  return Object.prototype.toString.call(params).slice(8, -1) === 'URLSearchParams';
}
/**
 * 拼接url
 *
 * @param {string} url 链接url
 * @param {Object} params 参数
 * @return 拼接好的url
 */


function buildURL(url, params) {
  if (!params) {
    return url;
  }

  var serializedParams = '';

  if (isURLSearchParams(params)) {
    serializedParams = params.toString();
  } else {
    var parts = [];
    Object.keys(params).forEach(function (key) {
      var val = params[key];

      if (val === null || typeof val === 'undefined') {
        return;
      }

      if (!Array.isArray(val)) {
        val = [val];
      }

      val.forEach(function (v) {
        if (v instanceof Date) {
          v = v.toISOString();
        } else if (_typeof(v) === 'object' && v !== null) {
          v = JSON.stringify(v);
        }

        parts.push("".concat(encode(key), "=").concat(encode(v)));
      });
    });
    serializedParams = parts.join('&');
  }

  if (serializedParams) {
    var hashmarkIndex = url.indexOf('#');

    if (hashmarkIndex !== -1) {
      url = url.slice(0, hashmarkIndex);
    }

    url += (url.indexOf('?') === -1 ? '?' : '&') + serializedParams;
  }

  return url;
}
/**
 * 请求方法
 * @param {Object} config 请求参数
 */


export function ajaxRequest(config) {
  var requestData = config.data;
  var requestHeaders = config.headers || {};
  var responseType = config.responseType || '';
  var callback = config.callback;
  var request = new XMLHttpRequest();
  request.open(config.method.toUpperCase(), buildURL(config.url, config.params), true); // Set the request timeout in MS

  request.timeout = config.timeout || 10 * 1000;

  function onloadend() {
    if (!request) {
      return;
    } // Prepare the response


    var responseHeaders = 'getAllResponseHeaders' in request ? parseHeaders(request.getAllResponseHeaders()) : null;
    var responseData = request.response;

    if (!responseType || responseType === 'text' || responseType === 'json') {
      try {
        responseData = JSON.parse(request.responseText);
      } catch (error) {
        responseData = request.responseText;
      }
    }

    var response = {
      data: responseData,
      status: request.status,
      statusText: request.statusText,
      headers: responseHeaders,
      config: config,
      request: request
    };

    if (response.status >= 200 && response.status < 300) {
      callback(null, response);
    } else {
      callback(new Error("Request failed with status code ".concat(response.status)));
    } // Clean up request


    request = null;
  }

  if ('onloadend' in request) {
    // Use onloadend if available
    request.onloadend = onloadend;
  } else {
    // Listen for ready state to emulate onloadend
    request.onreadystatechange = function handleLoad() {
      if (!request || request.readyState !== 4) {
        return;
      } // The request errored out and we didn't get a response, this will be
      // handled by onerror instead
      // With one exception: request that using file: protocol, most browsers
      // will return status as 0 even though it's a successful request


      if (request.status === 0 && !(request.responseURL && request.responseURL.indexOf('file:') === 0)) {
        return;
      } // readystate handler is calling before onerror or ontimeout handlers,
      // so we should call onloadend on the next 'tick'


      setTimeout(onloadend);
    };
  } // Handle browser request cancellation (as opposed to a manual cancellation)


  request.onabort = function handleAbort() {
    if (!request) {
      return;
    }

    callback(new Error('Request aborted')); // Clean up request

    request = null;
  }; // Handle low level network errors


  request.onerror = function handleError() {
    // Real errors are hidden from us by the browser
    // onerror should only fire if it's a network error
    callback(new Error('Network Error')); // Clean up request

    request = null;
  }; // Handle timeout


  request.ontimeout = function handleTimeout() {
    var timeoutErrorMessage = config.timeout ? "timeout of ".concat(config.timeout, "ms exceeded") : 'timeout exceeded';
    callback(new Error(timeoutErrorMessage)); // Clean up request

    request = null;
  }; // Add headers to the request


  if ('setRequestHeader' in request) {
    Object.keys(requestHeaders).forEach(function (key) {
      if (typeof requestData === 'undefined' && key.toLowerCase() === 'content-type') {
        // Remove Content-Type if data is undefined
        delete requestHeaders[key];
      } else {
        // Otherwise add header to the request
        request === null || request === void 0 ? void 0 : request.setRequestHeader(key, requestHeaders[key]);
      }
    });
  } // Add withCredentials to request if needed


  if (config.withCredentials) {
    request.withCredentials = !!config.withCredentials;
  } // Add responseType to request if needed


  if (responseType && responseType !== 'json') {
    request.responseType = responseType;
  }

  if (!requestData) {
    requestData = null;
  } // Send the request


  request.send(requestData);
}